import { useState } from "react"
import { presetToRange, addDays, PRESET_DAYS } from "../../lib/dateRange"
import type { DateRange, PeriodPreset } from "../../lib/dateRange"

export type { DateRange, PeriodPreset }

interface DateRangePickerProps {
  value: DateRange
  onChange: (range: DateRange) => void
  storageKey?: string
  allTimeStart?: string  // 全期間の開始日 (YYYY-MM-DD)。未指定時は 2000-01-01
  maxDays?: number  // 指定時、この日数を超える期間は選択不可（フリープランの45日制限用）
}

const PRESETS: { key: PeriodPreset; label: string }[] = [
  { key: "week",   label: "週" },
  { key: "month",  label: "月" },
  { key: "year",   label: "年" },
  { key: "all",    label: "全期間" },
  { key: "custom", label: "自由指定" },
]

const VALID_PRESETS = new Set<string>(["week", "month", "year", "all", "custom"])

const readStoredPreset = (storageKey: string): PeriodPreset | null => {
  try {
    const stored = sessionStorage.getItem(storageKey)
    if (!stored) return null
    const parsed = JSON.parse(stored)
    if (VALID_PRESETS.has(parsed.preset)) return parsed.preset as PeriodPreset
  } catch {}
  return null
}

const DateRangePicker = ({ value, onChange, storageKey, allTimeStart, maxDays }: DateRangePickerProps) => {
  const [activePreset, setActivePreset] = useState<PeriodPreset>(() => {
    if (storageKey) return readStoredPreset(storageKey) ?? "month"
    return "month"
  })

  const applyPreset = (preset: PeriodPreset) => {
    setActivePreset(preset)
    if (preset !== "custom") {
      onChange(presetToRange(preset, allTimeStart))
    }
    if (storageKey) {
      const data = preset === "custom"
        ? { preset, ...value }
        : { preset }
      sessionStorage.setItem(storageKey, JSON.stringify(data))
    }
  }

  const handleDateChange = (key: keyof DateRange, v: string) => {
    const newRange = { ...value, [key]: v }
    onChange(newRange)
    if (storageKey) {
      sessionStorage.setItem(storageKey, JSON.stringify({ preset: "custom", ...newRange }))
    }
  }

  return (
    <div className="date-range-picker">
      <div className="date-range-picker__presets">
        {PRESETS.map(p => {
          const disabled = p.key !== "custom" && maxDays != null && PRESET_DAYS[p.key] > maxDays
          return (
            <button
              key={p.key}
              className={`date-range-picker__preset-btn${activePreset === p.key ? " date-range-picker__preset-btn--active" : ""}`}
              onClick={() => applyPreset(p.key)}
              disabled={disabled}
              title={disabled ? `フリープランでは直近${maxDays}日までの期間を選択できます` : undefined}
            >
              {p.label}
              {disabled && <i className="bi bi-lock-fill date-range-picker__preset-lock" />}
            </button>
          )
        })}
      </div>
      {activePreset === "custom" && (
        <div className="date-range-picker__inputs">
          <input
            type="date"
            value={value.startDate}
            max={value.endDate}
            min={maxDays != null && value.endDate ? addDays(value.endDate, -(maxDays - 1)) : undefined}
            onChange={e => handleDateChange("startDate", e.target.value)}
          />
          <span>〜</span>
          <input
            type="date"
            value={value.endDate}
            min={value.startDate}
            max={maxDays != null && value.startDate ? addDays(value.startDate, maxDays - 1) : undefined}
            onChange={e => handleDateChange("endDate", e.target.value)}
          />
        </div>
      )}
    </div>
  )
}

export default DateRangePicker
